import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import ContactForm from "@/pages/about/components/ContactForm";
import { testimonials } from "@/mocks/testimonials";
import usePageMeta from "@/hooks/usePageMeta";

const principles = [
  {
    icon: "ri-shield-check-line",
    title: "只写已公开的部分",
    desc: "所有幕后内容都基于公开的制作访谈、主创分享与从业者的公开讲述整理，未公开的私人事务不写、不揣测。",
  },
  {
    icon: "ri-user-location-line",
    title: "不针对真实个人",
    desc: "我们关注的是制作过程本身，而不是把谁推上风口。涉及具体作品时以流程与事实为主，避免人身评价。",
  },
  {
    icon: "ri-book-open-line",
    title: "讲清楚为什么",
    desc: "不只是复述\"发生了什么\"，更要说清\"为什么这么做\"，让读者真正看懂镜头之外的工作逻辑。",
  },
  {
    icon: "ri-refresh-line",
    title: "有错就改",
    desc: "如果内容与事实有出入，欢迎通过联系表单指出。核实后会第一时间修订，并保留修订说明。",
  },
];

const workflow = [
  { step: "01", title: "收集线索", desc: "整理公开访谈、主创分享与读者投稿中的幕后信息，标记可核实的来源方向。" },
  { step: "02", title: "交叉核对", desc: "同一件事至少对照两个公开来源，确认时间、环节与参与方，避免孤证成文。" },
  { step: "03", title: "撰写与自检", desc: "由跟过组、了解现场流程的编辑执笔，落笔前再过一遍内容原则，删去主观揣测。" },
  { step: "04", title: "发布与修订", desc: "发布后持续接收反馈，若出现新的公开信息，会在原文中补充并说明修订点。" },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 黑料八卦官网内容原则与投稿联系",
    description:
      "黑料八卦官网专注综艺、影视、节目与剧组幕后内容，坚持只写已公开的信息、不针对真实个人。了解我们的内容原则、工作流程与投稿联系方式。",
    keywords: "黑料八卦,关于黑料八卦,黑料八卦内容原则,剧组幕后投稿,影视幕后联系",
    canonicalPath: "/about",
  });

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        name: "关于我们 - 黑料八卦",
        inLanguage: "zh-CN",
        url: "https://glmrz.cn/about",
        description:
          "黑料八卦官网的定位、发展理念、内容原则与联系方式介绍。",
      },
      {
        "@type": "Organization",
        name: "黑料八卦",
        url: "https://glmrz.cn/",
        email: "hello@heiliao-bagua.example",
        description: "专注综艺、影视与剧组幕后内容的娱乐资讯站点。",
      },
      {
        "@type": "ContactPage",
        name: "联系与投稿 - 黑料八卦",
        url: "https://glmrz.cn/about#contact",
        inLanguage: "zh-CN",
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <SiteHeader />

      <main>
        {/* 面包屑 */}
        <nav
          aria-label="面包屑导航"
          className="max-w-portal mx-auto px-4 md:px-6 pt-8 flex items-center gap-2 text-xs text-foreground-500"
        >
          <Link to="/" className="hover:text-accent-700 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          <span className="text-foreground-800 font-medium">关于我们</span>
        </nav>

        {/* 首屏 */}
        <section className="max-w-portal mx-auto px-4 md:px-6 pt-8 md:pt-12">
          <div className="rounded-lg bg-foreground-950 text-background-50 px-6 py-12 md:px-14 md:py-16">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.2em] text-primary-400 uppercase">
              <span className="w-6 h-px bg-primary-500" aria-hidden="true" />
              About Us
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-black leading-tight mt-4 max-w-3xl">
              我们只做一件事：把镜头之外的剧情补全
            </h1>
            <p className="text-base md:text-lg text-background-300 leading-relaxed mt-5 max-w-2xl">
              黑料八卦是一个专注综艺、影视、节目与剧组幕后的内容站点。拍摄现场发生了什么、
              剪辑台剪掉了什么，我们不生产传闻，只把已公开的制作故事讲清楚。
            </p>
            <div className="flex flex-wrap gap-6 mt-8">
              {[
                { num: "1080+", label: "幕后稿件" },
                { num: "6", label: "内容栏目" },
                { num: "每周 3-5", label: "更新节奏" },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="font-heading text-2xl md:text-3xl font-black text-primary-400">
                    {stat.num}
                  </p>
                  <p className="text-xs text-background-400 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 定位 */}
        <section className="max-w-portal mx-auto px-4 md:px-6 pt-12 md:pt-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.2em] text-accent-700 uppercase">
                <span className="w-6 h-px bg-accent-500" aria-hidden="true" />
                Positioning
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 mt-2">
                网站定位
              </h2>
            </div>
            <div className="lg:col-span-2 space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              <p>
                我们把目光放在作品成型之前的那些环节：选景、围读、布光、群戏调度、现场收音、
                剪辑取舍。这些内容平时很少被完整讲述，却决定了观众最终看到什么。
              </p>
              <p>
                因此这里既不是单纯的追星站点，也不是行业内部资料库。它更像一本关于「一部作品
                是怎么被做出来的」的观察笔记，写给真正对制作过程感兴趣的读者。
              </p>
              <p>
                我们相信，了解幕后不会削弱作品的魅力，反而会让那些看似理所当然的画面变得更有分量。
              </p>
            </div>
          </div>
        </section>

        {/* 内容原则 */}
        <section id="principles" className="max-w-portal mx-auto px-4 md:px-6 pt-12 md:pt-16 scroll-mt-28">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-6 h-px bg-accent-500" aria-hidden="true" />
            <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">
              内容原则
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {principles.map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-4 p-5 md:p-6 rounded-lg bg-background-100 border border-background-200"
              >
                <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-md bg-foreground-950 text-primary-400">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mt-2">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 工作流程 */}
        <section className="max-w-portal mx-auto px-4 md:px-6 pt-12 md:pt-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-6 h-px bg-accent-500" aria-hidden="true" />
            <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">
              我们怎么工作
            </h2>
          </div>
          <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {workflow.map((item) => (
              <li
                key={item.step}
                className="relative p-5 md:p-6 rounded-lg bg-secondary-50 border border-secondary-200"
              >
                <span className="font-heading text-3xl font-black text-secondary-300">
                  {item.step}
                </span>
                <h3 className="font-heading text-base font-bold text-foreground-950 mt-2">
                  {item.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed mt-2">{item.desc}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* 口碑 */}
        <section className="bg-background-100 border-y border-background-200 mt-12 md:mt-16">
          <div className="max-w-portal mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-6 h-px bg-accent-500" aria-hidden="true" />
              <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">
                读者口碑
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {testimonials.slice(0, 3).map((item) => (
                <figure
                  key={item.id}
                  className="flex flex-col p-5 md:p-6 rounded-lg bg-background-50 border border-background-200"
                >
                  <blockquote className="text-sm text-foreground-700 leading-relaxed flex-1">
                    {item.text}
                  </blockquote>
                  <figcaption className="flex items-center gap-3 mt-5 pt-4 border-t border-background-200">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
                      <i className={`${item.avatar} text-xl`} />
                    </span>
                    <span>
                      <span className="block text-sm font-bold text-foreground-900">{item.name}</span>
                      <span className="block text-xs text-foreground-500 mt-0.5">{item.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* 联系方式 */}
        <section id="contact" className="max-w-portal mx-auto px-4 md:px-6 pt-12 md:pt-16 scroll-mt-28">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10">
            <div className="lg:col-span-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.2em] text-accent-700 uppercase">
                <span className="w-6 h-px bg-accent-500" aria-hidden="true" />
                Contact
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 mt-2">
                联系与投稿
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mt-3">
                有幕后线索、内容纠错或合作意向，都可以通过右侧表单告诉我们。我们会在几个工作日内回复。
              </p>

              <dl className="mt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-background-100 border border-background-200 text-accent-600">
                    <i className="ri-mail-line text-xl" />
                  </span>
                  <div>
                    <dt className="text-sm font-bold text-foreground-900">投稿与合作邮箱</dt>
                    <dd className="text-sm text-foreground-600 mt-0.5">
                      hello@heiliao-bagua.example
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-background-100 border border-background-200 text-accent-600">
                    <i className="ri-time-line text-xl" />
                  </span>
                  <div>
                    <dt className="text-sm font-bold text-foreground-900">回复时间</dt>
                    <dd className="text-sm text-foreground-600 mt-0.5">
                      工作日 10:00 - 18:00，一般 3 个工作日内回复
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-background-100 border border-background-200 text-accent-600">
                    <i className="ri-information-line text-xl" />
                  </span>
                  <div>
                    <dt className="text-sm font-bold text-foreground-900">投稿提示</dt>
                    <dd className="text-sm text-foreground-600 mt-0.5">
                      请注明作品类型与大致阶段，以及你愿意公开的信息范围
                    </dd>
                  </div>
                </div>
              </dl>
            </div>

            <div className="lg:col-span-3">
              <ContactForm />
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}